import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../lib/useSEO';
import { ArrowLeft, Calendar, Tag, ArrowRight, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import api from '../lib/api';
import { useAuthStore } from '../lib/auth';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function Blog() {
  useSEO('Blog | JepPrep', 'Jeopardy strategies, trivia science, and expert study guides to help you train smarter and compete harder.', { canonical: 'https://jepprep.com/blog' });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await api.get('/api/blog/posts');
        setPosts(response.data.posts);
      } catch (error) {
        console.error('Failed to fetch blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Inject ItemList JSON-LD for blog listing
  useEffect(() => {
    if (!posts.length) return;
    const itemListJsonLd = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "JepPrep Blog",
      "description": "Jeopardy strategies, trivia science, and expert study guides.",
      "url": "https://jepprep.com/blog",
      "numberOfItems": posts.length,
      "itemListElement": posts.map((post, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `https://jepprep.com/blog/${post.slug}`,
        "name": post.title
      }))
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-blog-list-schema', 'true');
    script.textContent = JSON.stringify(itemListJsonLd);
    document.head.appendChild(script);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, [posts]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="blog-page">
      {/* Header */}
      <header className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="JepPrep" className="h-10" />
          </Link>
          {user ? (
            <Link to="/dashboard">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-gold-400))] font-semibold text-sm" data-testid="blog-dashboard-btn">
                Dashboard
              </Button>
            </Link>
          ) : (
            <Link
              to="/login"
              className="text-sm text-[hsl(var(--brand-gold-500))] hover:text-white transition-colors font-medium"
              data-testid="blog-login-link"
            >
              Sign In
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        {/* Hero */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-8 h-8 text-[hsl(var(--brand-gold-500))]" />
            <h1 className="text-4xl sm:text-5xl font-bold text-[hsl(var(--brand-gold-500))]">The JepPrep Blog</h1>
          </div>
          <p className="text-lg text-gray-400 max-w-2xl">
            Strategies, science, and stories to help you master trivia. Whether you're a casual player or aspiring contestant, level up your game.
          </p>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[hsl(var(--brand-gold-500))]"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">No posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="blog-posts-list">
            {posts.map((post) => (
              <Link to={`/blog/${post.slug}`} key={post.slug} className="block group" data-testid={`blog-post-card-${post.slug}`}>
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] hover:border-[hsl(var(--brand-gold-500))]/40 transition-all duration-300 group-hover:translate-y-[-2px]">
                  <CardContent className="p-6 sm:p-8">
                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(post.published_at)}</span>
                      <span className="text-gray-700">|</span>
                      <span>{post.author}</span>
                    </div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-[hsl(var(--brand-gold-500))] transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-400 mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2 flex-wrap">
                        {post.tags?.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs border-[hsl(var(--border))] text-gray-500">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <span className="text-[hsl(var(--brand-gold-500))] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read more <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-16 text-center p-8 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/30">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to put these tips into practice?</h2>
          <p className="text-gray-400 mb-6">Start training with JepPrep — free trivia practice with real game show clues.</p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-bold rounded-lg hover:bg-[hsl(var(--brand-gold-400))] transition-colors"
            data-testid="blog-cta-register"
          >
            Get Started Free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-[hsl(var(--border))]">
          <div className="flex flex-col items-center gap-3">
            <img src={LOGO_URL} alt="JepPrep" className="h-10" />
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <span>|</span>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/blog" className="hover:text-white transition-colors text-[hsl(var(--brand-gold-500))]">Blog</Link>
            </div>
            <p className="text-gray-600 text-xs text-center max-w-xl">
              © 2026 JepPrep. Not affiliated with Jeopardy!, Sony Pictures Television, or their subsidiaries.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
